import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Send, CheckCircle2 } from "lucide-react";
import { LandingHeader } from "../components/landing/LandingHeader";
import { LandingFooter } from "../components/landing/LandingFooter";
import { colors } from "../components/landing/colors";

export default function KontaktPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "", firma: "", email: "", telefon: "", nachricht: "", dsgvo: false,
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const scrollToSection = (id) => {
    setMobileMenuOpen(false);
    window.location.href = `/#${id}`;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setIsSubmitting(false);
    setIsSubmitted(true);
  };

  return (
    <div className="min-h-screen font-['Inter',system-ui,sans-serif]" style={{ backgroundColor: colors.white }}>
      <LandingHeader
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        scrollToSection={scrollToSection}
      />

      {/* ============ HERO ============ */}
      <section style={{ backgroundColor: colors.darkBlue }} data-testid="kontakt-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-40 md:pt-52 pb-16 md:pb-24">
          <h1 className="text-white font-bold text-[36px] md:text-[52px] lg:text-[60px] leading-[1.1] tracking-tight">
            Kontakt
          </h1>
          <p className="mt-4 text-white/45 text-lg md:text-xl leading-relaxed max-w-md">
            Wir freuen uns auf Ihr Projekt.
          </p>
        </div>
      </section>

      {/* ============ KONTAKTBEREICH ============ */}
      {/* ============ KONTAKTKARTEN ============ */}
      <section className="py-20 md:py-32" style={{ backgroundColor: colors.white }} data-testid="kontakt-cards">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-[#94a3b8] text-xs uppercase tracking-[0.2em] mb-12">
            Ansprechpartner
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 mb-28">
            {[
              { name: "Florian Trautmann", tel: "+49 178 4061676", telRaw: "+491784061676" },
              { name: "Ronny Pforr", tel: "+49 172 3023898", telRaw: "+491723023898" },
            ].map((person) => (
              <div key={person.name} className="space-y-6" data-testid={`kontakt-card-${person.name.split(" ")[1].toLowerCase()}`}>
                <p className="text-gray-900 text-2xl md:text-[28px] font-semibold tracking-tight">
                  {person.name}
                </p>

                <div className="space-y-4 text-[15px] leading-relaxed">
                  <div>
                    <p className="text-gray-400 text-[13px] mb-1">Unternehmen</p>
                    <p className="text-gray-700">TGAone OHG</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-[13px] mb-1">Adresse</p>
                    <p className="text-gray-700">Dammersbacher Straße 7<br />36088 Hünfeld</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-[13px] mb-1">Telefon</p>
                    <a href={`tel:${person.telRaw}`} className="text-gray-700 hover:text-gray-900 transition-colors">
                      {person.tel}
                    </a>
                  </div>
                  <div>
                    <p className="text-gray-400 text-[13px] mb-1">E-Mail</p>
                    <a href="mailto:info@tgaone.de" className="text-gray-700 hover:text-gray-900 transition-colors">
                      info@tgaone.de
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* ============ FORMULAR ============ */}
          <div className="max-w-2xl">
              {isSubmitted ? (
                <div className="border border-green-200 bg-green-50/50 p-10 text-center" data-testid="kontakt-success">
                  <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto mb-5" />
                  <h3 className="font-semibold text-gray-900 text-lg mb-2">Nachricht gesendet</h3>
                  <p className="text-gray-500 text-[15px]">
                    Vielen Dank für Ihre Anfrage. Wir melden uns schnellstmöglich bei Ihnen.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="kontakt-form">
                  {/* Name */}
                  <div>
                    <label className="block text-[13px] font-medium text-gray-900 mb-2">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text" name="name" required
                      placeholder="Ihr vollständiger Name"
                      value={formData.name} onChange={handleChange}
                      className="w-full h-12 px-4 bg-white border border-gray-200 text-gray-900 text-[15px] placeholder:text-gray-300 focus:outline-none focus:border-gray-900 transition-colors"
                      data-testid="kontakt-name"
                    />
                  </div>

                  {/* Firma */}
                  <div>
                    <label className="block text-[13px] font-medium text-gray-900 mb-2">Firma</label>
                    <input
                      type="text" name="firma"
                      placeholder="Firmenname (optional)"
                      value={formData.firma} onChange={handleChange}
                      className="w-full h-12 px-4 bg-white border border-gray-200 text-gray-900 text-[15px] placeholder:text-gray-300 focus:outline-none focus:border-gray-900 transition-colors"
                      data-testid="kontakt-firma"
                    />
                  </div>

                  {/* E-Mail */}
                  <div>
                    <label className="block text-[13px] font-medium text-gray-900 mb-2">
                      E-Mail <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email" name="email" required
                      placeholder="ihre@email.de"
                      value={formData.email} onChange={handleChange}
                      className="w-full h-12 px-4 bg-white border border-gray-200 text-gray-900 text-[15px] placeholder:text-gray-300 focus:outline-none focus:border-gray-900 transition-colors"
                      data-testid="kontakt-email"
                    />
                  </div>

                  {/* Telefon */}
                  <div>
                    <label className="block text-[13px] font-medium text-gray-900 mb-2">Telefon</label>
                    <input
                      type="tel" name="telefon"
                      placeholder="+49 123 456789 (optional)"
                      value={formData.telefon} onChange={handleChange}
                      className="w-full h-12 px-4 bg-white border border-gray-200 text-gray-900 text-[15px] placeholder:text-gray-300 focus:outline-none focus:border-gray-900 transition-colors"
                      data-testid="kontakt-telefon"
                    />
                  </div>

                  {/* Nachricht */}
                  <div>
                    <label className="block text-[13px] font-medium text-gray-900 mb-2">
                      Nachricht <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="nachricht" required rows={5}
                      placeholder="Beschreiben Sie kurz Ihr Anliegen..."
                      value={formData.nachricht} onChange={handleChange}
                      className="w-full px-4 py-3 bg-white border border-gray-200 text-gray-900 text-[15px] placeholder:text-gray-300 focus:outline-none focus:border-gray-900 transition-colors resize-none"
                      data-testid="kontakt-nachricht"
                    />
                  </div>

                  {/* DSGVO */}
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox" name="dsgvo" id="dsgvo" required
                      checked={formData.dsgvo} onChange={handleChange}
                      className="mt-0.5 w-4 h-4 border border-gray-300 accent-gray-900"
                      data-testid="kontakt-dsgvo"
                    />
                    <label htmlFor="dsgvo" className="text-[13px] text-gray-400 leading-relaxed">
                      Ich stimme zu, dass meine Angaben zur Kontaktaufnahme gespeichert werden.
                      Weitere Informationen in der{" "}
                      <Link to="/datenschutz" className="text-gray-900 underline hover:no-underline">
                        Datenschutzerklärung
                      </Link>
                      . <span className="text-red-500">*</span>
                    </label>
                  </div>

                  {/* Submit */}
                  <button
                    type="submit" disabled={isSubmitting}
                    className="w-full h-14 font-medium text-[15px] flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                    style={{
                      backgroundColor: colors.darkBlue,
                      color: colors.white,
                    }}
                    data-testid="kontakt-submit"
                  >
                    {isSubmitting ? "Wird gesendet..." : (
                      <>Nachricht senden <ArrowRight className="w-4 h-4" /></>
                    )}
                  </button>
                </form>
              )}
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
}
